<template>
  <div :class="cn('card', props.class)">
    <div class="card-body">
      <template v-if="data">
        <strong class="card-title d-flex mb-0">{{ data.title }}</strong>
        <small v-if="data.subtitle" class="text-muted d-block">{{ data.subtitle }}</small>
      </template>
      <div v-else-if="loading" class="tw:h-4 tw:w-1/2 tw:rounded tw:bg-zinc-100 tw:animate-pulse" aria-hidden="true"></div>

      <div class="mt-2">
        <p v-if="error" class="tw:mb-0 tw:text-sm tw:text-rose-600">
          {{ $t('This figure could not be loaded.') }}
          <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="load()">{{ $t('Retry') }}</button>
        </p>
        <LoadingOverlay v-else :active="loading">
          <template v-if="data">
            <p v-if="isEmpty" class="tw:mb-0 tw:text-sm tw:text-zinc-500">{{ $t('No data for these filters.') }}</p>
            <ValueMetric v-else-if="data.type === 'value'" :metric="data" />
            <PercentageMetric v-else-if="data.type === 'percentage'" :metric="data" />
            <DoughnutMetric v-else-if="data.type === 'distribution' && display === 'doughnut'" :metric="data" />
            <ProgressBarMetric v-else-if="data.type === 'distribution'" :metric="data" :collection="collection" :legend-type="legendType" />
            <TrendMetric v-else-if="data.type === 'trend'" :metric="data" />
          </template>
          <div v-else class="tw:h-24" aria-hidden="true"></div>
        </LoadingOverlay>
      </div>

      <small v-if="data?.note" class="text-muted font-italic d-block mt-2">{{ data.note }}</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type HTMLAttributes } from 'vue';
import LoadingOverlay from '@/components/ui/LoadingOverlay.vue';
import { useMetric } from '@/composables/useMetric';
import { cn } from '@/shadcn/lib/utils';
import ValueMetric from './ValueMetric.vue';
import PercentageMetric from './PercentageMetric.vue';
import DoughnutMetric from './DoughnutMetric.vue';
import ProgressBarMetric from './ProgressBarMetric.vue';
import TrendMetric from './TrendMetric.vue';
import { METRIC_BOARD_KEY, type MetricDisplay, type MetricLegendType } from './metrics';

const props = withDefaults(defineProps<{
  collection: string
  metric: string
  /** Only read for a distribution, which can be drawn either way. */
  display?: MetricDisplay
  legendType?: MetricLegendType | MetricLegendType[]
  /** Filters this card adds to the ones of its board. */
  filters?: Record<string, string>
  interval?: string
  limit?: number
  class?: HTMLAttributes['class']
}>(), {
  display: 'progress-bar',
  legendType: () => ['percentage'],
  filters: () => ({}),
  interval: undefined,
  limit: undefined,
  class: undefined,
});

const board = inject(METRIC_BOARD_KEY, { filters: {} });

const legendType = computed(() => Array.isArray(props.legendType) ? props.legendType : [props.legendType]);

const { data, loading, error, load } = useMetric(
  () => props.collection,
  () => props.metric,
  () => ({
    filters: { ...board.filters, ...props.filters },
    interval: props.interval,
    limit: props.limit,
  }),
);

const isEmpty = computed(() => data.value?.type === 'distribution' && data.value.value.rows.length === 0);
</script>
